// Fee-frame helpers for TP-Scout-Requests.
//
// The fee frame is realism GUIDANCE, never a negotiation figure (Brief §2). Design
// T7: "Presets internally = Custom with fixed values: one data model." The backend
// (dashboard/scout_request.py) persists ONE shape — {kind, currency, min, max} —
// so the presets below are just pre-filled ranges. No currency conversion ever
// happens (the outside-frame check on the server withholds the flag across
// currencies); the FE only formats what the server returns.

// The four currencies the custom frame offers (design 7a). Symbols per design:
// €/£/$ render tight; CHF renders as a text prefix ("up to CHF 800k").
export const FEE_FRAME_CURRENCIES = [
  { value: "CHF", label: "CHF" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "USD", label: "USD" },
];

const CURRENCY_SYMBOL = { EUR: "€", GBP: "£", USD: "$", CHF: "CHF " };

// Design 7a preset list (order + copy verbatim). Each preset resolves to the
// single persisted shape the write serializer accepts. Presets are shown with €
// (design uses the euro symbol); the custom path lets the club pick a currency.
export const FEE_FRAME_PRESETS = [
  { id: "free_agents", label: "Free agents / end of contract", payload: { fee_frame_kind: "free_agents" } },
  { id: "up_to_1m", label: "Up to €1M", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: null, fee_frame_max: 1000000 } },
  { id: "1_5m", label: "€1-5M", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 1000000, fee_frame_max: 5000000 } },
  { id: "5m_plus", label: "€5M+", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 5000000, fee_frame_max: null } },
  { id: "open", label: "Open (no frame)", payload: { fee_frame_kind: "open" } },
];

// Parse a shorthand amount into a whole integer, or null when it is not a number.
// Accepts "750k", "1.2M", "1'200'000", "1,200,000", "800000", a leading currency
// symbol, and surrounding whitespace. k = 1e3, m = 1e6, b = 1e9 (case-insensitive).
//
// The APOSTROPHE was added when the platform's thousands separator became one
// (Marin, 2026-08-02). This function is the inverse of what the user reads
// elsewhere on the screen, so a figure copied from a card and pasted into a fee
// frame has to come back as the same number — without this it parsed
// "1'200'000" as nothing at all. The comma stays accepted too: it is what the
// platform printed until this change, and old copy still has to work.
export const parseFeeShorthand = (raw) => {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim().toLowerCase();
  if (!s) return null;
  // Strip currency symbols/codes, thousands separators and spaces — keep digits,
  // one decimal point and a single k/m/b multiplier suffix.
  s = s.replace(/[€£$]/g, "").replace(/chf|eur|gbp|usd/g, "").replace(/[\s,']/g, "");
  const match = s.match(/^(\d+(?:\.\d+)?)([kmb])?$/);
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (Number.isNaN(value)) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9 }[match[2]] || 1;
  return Math.round(value * mult);
};

// Compact an integer amount back to the design's shorthand ("1M", "1.2M", "800k",
// "500"). Trailing ".0" is dropped so "1M" never reads "1.0M".
export const formatFeeAmount = (amount) => {
  if (amount === null || amount === undefined) return "";
  const n = Number(amount);
  if (Number.isNaN(n)) return "";
  const compact = (value, suffix) => {
    const fixed = value.toFixed(1).replace(/\.0$/, "");
    return `${fixed}${suffix}`;
  };
  if (Math.abs(n) >= 1e6) return compact(n / 1e6, "M");
  if (Math.abs(n) >= 1e3) return compact(n / 1e3, "k");
  return String(n);
};

// Render a fee_frame object ({kind, currency, min, max}) as the calm pill text the
// design shows: "open", "free agents", "up to CHF 800k", "€1-5M", "from $2M".
// Returns "" for a missing/empty frame so callers can hide the pill entirely.
export const formatFeeFrame = (frame) => {
  if (!frame || !frame.kind) return "";
  if (frame.kind === "open") return "open";
  if (frame.kind === "free_agents") return "free agents";
  if (frame.kind !== "range") return "";
  const sym = CURRENCY_SYMBOL[(frame.currency || "").toUpperCase()] || "";
  const { min, max } = frame;
  if (min == null && max == null) return "open";
  if (min == null) return `up to ${sym}${formatFeeAmount(max)}`;
  if (max == null) return `from ${sym}${formatFeeAmount(min)}`;
  // Two bounds: symbol once, and when both share a magnitude suffix drop it from
  // the min so a range reads like the design's "€1-5M" (not "€1M–5M").
  const minStr = formatFeeAmount(min);
  const maxStr = formatFeeAmount(max);
  const sufMin = minStr.match(/[Mk]$/)?.[0];
  const sufMax = maxStr.match(/[Mk]$/)?.[0];
  const minPart = sufMin && sufMin === sufMax ? minStr.slice(0, -1) : minStr;
  return `${sym}${minPart}–${maxStr}`;
};

// The full pill label used on cards ("Fee frame: up to €1M"). Hidden when empty.
export const feeFramePillLabel = (frame) => {
  const body = formatFeeFrame(frame);
  return body ? `Fee frame: ${body}` : "";
};

// Client-side pre-send mirror of the server's outside-fee-frame check
// (dashboard/scout_request.compute_outside_fee_frame). Used only to paint the calm
// pre-send warning (design 2c) when the search result carries a market value; the
// server recomputes and PERSISTS the authoritative flag on submit. Returns true
// only when an HONEST comparison is possible AND the value sits outside the frame:
//   * the frame must be a numeric range with a currency and at least one bound;
//   * the player must have a market value in the SAME currency (no conversion —
//     an apples-to-oranges compare would be dishonest → returns false).
export const computeOutsideFrame = (frame, marketValue, marketCurrency) => {
  if (marketValue === null || marketValue === undefined) return false;
  if (!frame || frame.kind !== "range") return false;
  if (frame.min == null && frame.max == null) return false;
  const frameCcy = String(frame.currency || "").trim().toUpperCase();
  const mvCcy = String(marketCurrency || "").trim().toUpperCase();
  if (!frameCcy || frameCcy !== mvCcy) return false;
  const mv = Number(marketValue);
  if (Number.isNaN(mv)) return false;
  if (frame.min != null && mv < frame.min) return true;
  if (frame.max != null && mv > frame.max) return true;
  return false;
};

// "est. value €4M" line for a picked player row (design 2c). Empty when no value.
export const formatEstValue = (marketValue, marketCurrency) => {
  if (marketValue === null || marketValue === undefined) return "";
  const sym = CURRENCY_SYMBOL[String(marketCurrency || "").toUpperCase()] || "";
  return `est. value ${sym}${formatFeeAmount(marketValue)}`;
};
